/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";

import template from "./ns-action-menu-segmented.html";
import { FlattenedNodesObserver } from "@polymer/polymer/lib/utils/flattened-nodes-observer";

/**
 * <p>
 * A dropdown intended for use with a list of actions, rather than a list of options.
 * It is intended to be used in conjunction with `<paper-item>`, as shown in the examples
 * below. Each item in the list is to be given an onclick callback function to perform the
 * action that item represents.
 *
 * This component differs from `<ns-action-menu>` in that this component is intended to have
 * a default selectable action. The item given the attribute of `slot="default"` will be
 * made the default action for this component.
 * </p>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-action-menu-segmented>
 *       <paper-item slot="default" onclick="doDefaultAction()">Do Default Action</paper-item>
 *       <paper-item onclick="doFirstAction()">Do An Action</paper-item>
 *       <paper-item onclick="doSecondAction()">Do Another Action</paper-item>
 *       <paper-item onclick="doThirdAction()">Do Something Else</paper-item>
 *     </ns-action-menu-segmented>
 *     <br/>
 *     <ns-action-menu-segmented disabled>
 *       <paper-item slot="default" onclick="doDefaultAction()">Do Default Action</paper-item>
 *       <paper-item onclick="doFirstAction()">Do An Action</paper-item>
 *     </ns-action-menu-segmented>
 *     <ns-action-menu-segmented theme="secondary">
 *       <paper-item slot="default" onclick="doDefaultAction()">Do Default Action</paper-item>
 *       <paper-item onclick="doFirstAction()">Do An Action</paper-item>
 *     </ns-action-menu-segmented>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *   <div slot="markdown-html">
 *     <script type="text/markdown">
 *          ### Attributes
 *          `<ns-action-menu-segmented>` provides the following attributes:
 *          `theme`
 *          This attribute selects the general styling theme to use with this component.
 *          Valid values are "primary", "primary-hit", "primary-inverse",
 *          "secondary", "secondary-inverse", "transparent, "transparent-hit",
 *          "transparent-inverse".
 *
 *          `disabled`
 *          This Boolean attribute indicates that the user cannot interact with this component.
 *          If this attribute is not set, the component is enabled.
 *
 *          `size`
 *          This attribute selects the vertical size of the component. A value of "short" sets the
 *          component to the Nu Skin short input height of 30px. If this attribute is not set,
 *          the component defaults to the Nu Skin standard input height of 45px.
 *
 *          `open`
 *          This attribute selects in which direction the option menu should open. A value of "up"
 *          sets the option menu to open upward, above the component. If this attribute is not set,
 *          the option menu defaults to open downward.
 *
 *          `opened`
 *          This read-only Boolean attribute indicates whether the option menu is currently open.
 *
 *          ### Styling
 *          `<ns-action-menu-segmented>` provides the following custom properties and mixins for styling:
 *          Property                              │ Description                                                    │ Default Value
 *          ──────────────────────────────────────┼────────────────────────────────────────────────────────────────┼──────────────
 *          `--font-button`                       │ overrides styles applied to default action and dropdown button │ '{}'
 *          `--ns-action-menu-segmented-dropdown  │ overrides styles applied to dropdown section                   │ '{}'
 *
 *     </script>
 *   </div>
 * </marked-element>
 *
 */
export class NsActionMenuSegmented extends PolymerElement {
  static get is() {
    return "ns-action-menu-segmented";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * If true, the user cannot interact with this component.
       */
      disabled: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        observer: "_setDefaultDisabled"
      },
      /**
       * If true, this component's option menu is currently opened.
       */
      opened: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        notify: true
      }
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this._childNodesObserver = new FlattenedNodesObserver(this, (info) => {
      // Find the node representing the default option
      if (!this._defaultOptionNode) {
        this._defaultOptionNode = info.addedNodes.find((node) => {
          return (
            node.nodeType === Node.ELEMENT_NODE &&
            node.getAttribute("slot") === "default"
          );
        });
      }

      // Reflect this component's disabled status to the default option node
      this._setDefaultDisabled();
    });
  }

  /**
   * Opens the action list
   * @public
   */
  open() {
    if (!this.disabled) {
      this.$.dropdownContent.show();
      this.$.blurTarget.focus();
      this.opened = true;
    }
  }

  /**
   * Closes the action list
   * @public
   */
  close() {
    this.$.dropdownContent.hide();
    this.opened = false;
  }

  /**
   * Toggles the action list between opened and closed
   * @public
   */
  toggle() {
    if (!this.disabled && !this._blurring) {
      if (this.opened) this.close();
      else this.open();
    }
  }

  /**
   * Closes the dropdown on blur
   * @private
   */
  _onBlur() {
    this._blurring = true;
    setTimeout(() => {
      this.close();
      this.$.dropdownListbox.selected = -1;
      this._blurring = false;
    }, 150);
  }

  /**
   * Propagates changes to this component's disabled state to the default option element
   * @private
   */
  _setDefaultDisabled() {
    if (this._defaultOptionNode) {
      if (this.disabled) {
        this._defaultOptionNode.setAttribute("disabled", "true");
      } else {
        this._defaultOptionNode.removeAttribute("disabled");
      }
    }
  }

  _onDefaultClick() {
    if (this._defaultOptionNode) this._defaultOptionNode.blur();
  }
}

customElements.define(NsActionMenuSegmented.is, NsActionMenuSegmented);
